import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaAtom, FaCalculator, FaEraser } from 'react-icons/fa';

const EinsteinMassEnergyCalculator = () => {
  const [mass, setMass] = useState('');
  const [energy, setEnergy] = useState('');
  const [speedOfLight, setSpeedOfLight] = useState('299792458'); // Speed of light in m/s
  const [showResult, setShowResult] = useState(false);
  const [error, setError] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateEnergy = () => {
    if (!mass) {
      setError('Please enter a mass value');
      return;
    }

    try {
      const m = parseFloat(mass);
      if (isNaN(m) || m <= 0) {
        setError('Please enter a valid positive mass value');
        return;
      }

      const c = parseFloat(speedOfLight);
      const E = m * c * c;
      setEnergy(E.toFixed(2));
      setShowResult(true);
      setError('');
      setShowGraph(true);
    } catch (err) {
      setError('An error occurred during calculation');
    }
  };

  const calculateMass = () => {
    if (!energy) {
      setError('Please enter an energy value');
      return;
    }

    try {
      const E = parseFloat(energy);
      if (isNaN(E) || E <= 0) {
        setError('Please enter a valid positive energy value');
        return;
      }

      const c = parseFloat(speedOfLight);
      const m = E / (c * c);
      setMass(m.toFixed(2));
      setShowResult(true);
      setError('');
      setShowGraph(true);
    } catch (err) {
      setError('An error occurred during calculation');
    }
  };

  const resetCalculation = () => {
    setMass('');
    setEnergy('');
    setShowResult(false);
    setError('');
    setShowGraph(false);
  };

  const generateGraphData = () => {
    if (!mass || !energy) return null;

    const m = parseFloat(mass);
    const E = parseFloat(energy);
    const c = parseFloat(speedOfLight);

    const series = [
      {
        name: 'Energy vs Mass',
        data: Array.from({ length: 20 }, (_, i) => {
          const currentMass = (i - 10) * (m / 10);
          const currentEnergy = currentMass * c * c;
          return {
            x: currentMass,
            y: currentEnergy
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Mass (kg)',
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Energy (J)',
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        }
      },
      title: {
        text: 'Energy vs Mass',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#FF4560'],
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Zero Mass'
            }
          },
          {
            x: m,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Mass = ' + m.toFixed(2) + 'kg'
            }
          }
        ],
        yaxis: [
          {
            y: E,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Energy = ' + E.toFixed(2) + 'J'
            }
          }
        ]
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(2) + 'J';
          }
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-50 to-indigo-50">
      <Helmet>
        <title>Einstein Mass-Energy Calculator | E=mc² Calculator</title>
        <meta name="description" content="Calculate the relationship between mass and energy using Einstein's famous equation E=mc². Convert mass to energy and vice versa with this powerful physics calculator." />
        <meta name="keywords" content="einstein calculator, mass energy calculator, physics calculator, e=mc2 calculator, energy calculator" />
        <meta property="og:title" content="Einstein Mass-Energy Calculator | E=mc² Calculator" />
        <meta property="og:description" content="Calculate the relationship between mass and energy using Einstein's famous equation E=mc². Convert mass to energy and vice versa with this powerful physics calculator." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Einstein Mass-Energy Calculator | E=mc² Calculator" />
        <meta name="twitter:description" content="Calculate the relationship between mass and energy using Einstein's famous equation E=mc². Convert mass to energy and vice versa with this powerful physics calculator." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/einstein-mass-energy" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaAtom />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Einstein Mass-Energy Calculator</h1>
          <p className="text-xl text-gray-600">Calculate E=mc²</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Mass (kg)</label>
                <input
                  type="number"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter mass (kilograms)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Energy (J)</label>
                <input
                  type="number"
                  value={energy}
                  onChange={(e) => setEnergy(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter energy (joules)"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={calculateEnergy}
                  className="flex-1 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  <FaCalculator className="mr-2" /> Calculate Energy
                </button>
                <button
                  onClick={calculateMass}
                  className="flex-1 bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  <FaCalculator className="mr-2" /> Calculate Mass
                </button>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {showResult && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <p className="text-lg font-semibold">Result:</p>
                  {mass && <p>Mass: {mass} kg</p>}
                  {energy && <p>Energy: {energy} J</p>}
                </div>
              )}

              <button
                onClick={resetCalculation}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
              >
                <FaEraser className="mr-2" /> Reset Calculation
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Important Points</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Einstein's famous equation E=mc² shows the relationship between mass and energy.</li>
              <li>The speed of light (c) is approximately 299,792,458 meters per second.</li>
              <li>Mass can be converted to energy and vice versa using this equation.</li>
              <li>This calculator helps visualize how mass and energy are related in special relativity.</li>
              <li>The relationship is non-linear, as energy increases quadratically with mass.</li>
            </ul>
          </div>
        </div>

        {showGraph && (
          <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Visualization</h2>
            <div className="w-full">
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
            </div>
          </div>
        )}

        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">How to Use</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Enter either a mass value (in kilograms) or an energy value (in joules) in the respective input field.</li>
                <li>Click the appropriate button to calculate either energy or mass:</li>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>"Calculate Energy" - Converts mass to energy using E=mc²</li>
                  <li>"Calculate Mass" - Converts energy to mass using m=E/c²</li>
                </ul>
                <li>View the results in the display area below the buttons.</li>
                <li>Use the "Reset Calculation" button to clear all values and start over.</li>
                <li>Explore the visualization graph to see the relationship between mass and energy.</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Dual Calculation Modes</h3>
                    <p className="text-gray-600">Calculate both mass to energy and energy to mass conversions.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Interactive Visualization</h3>
                    <p className="text-gray-600">View the relationship between mass and energy in a dynamic graph.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Real-time Error Handling</h3>
                    <p className="text-gray-600">Get instant feedback for invalid inputs or calculation errors.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Responsive Design</h3>
                    <p className="text-gray-600">Works perfectly on both desktop and mobile devices.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">FAQ</h2>
            <div className="space-y-4">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900">What is E=mc²?</h3>
                  <span className="transition-all duration-300 group-open:rotate-180">
                    <svg className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </summary>
                <p className="p-4 text-gray-600">E=mc² is Einstein's famous equation that shows the relationship between mass and energy. It states that energy (E) equals mass (m) times the speed of light squared (c²).</p>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900">Why is the speed of light squared?</h3>
                  <span className="transition-all duration-300 group-open:rotate-180">
                    <svg className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </summary>
                <p className="p-4 text-gray-600">The speed of light squared (c²) is a conversion factor that shows how much energy is contained in a given amount of mass. It's a very large number, which means a small amount of mass can be converted into a huge amount of energy.</p>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900">What are some real-world applications?</h3>
                  <span className="transition-all duration-300 group-open:rotate-180">
                    <svg className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </summary>
                <p className="p-4 text-gray-600">E=mc² has applications in nuclear physics, particle physics, and astrophysics. It explains how nuclear power plants work, why stars shine, and the energy released in nuclear reactions.</p>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900">How accurate are the calculations?</h3>
                  <span className="transition-all duration-300 group-open:rotate-180">
                    <svg className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </summary>
                <p className="p-4 text-gray-600">The calculations are mathematically accurate based on the input values. However, for extremely precise scientific calculations, consider using specialized physics software.</p>
              </details>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EinsteinMassEnergyCalculator;
